/**
 * Lightweight client-side device detection used to tailor recovery
 * instructions when URL extraction fails.
 */
export type DeviceOS = "ios" | "android" | "mac" | "windows" | "linux" | "unknown";

export const detectOS = (): DeviceOS => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const platform = (navigator as any).platform || "";

  // iPadOS 13+ reports as Mac with touch — disambiguate.
  const isIPad =
    /iPad/.test(ua) ||
    (platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);

  if (/iPhone|iPod/.test(ua) || isIPad) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Mac/i.test(platform)) return "mac";
  if (/Win/i.test(platform)) return "windows";
  if (/Linux/i.test(platform)) return "linux";
  return "unknown";
};

export const isMobile = (): boolean => {
  const userAgentDataMobile = (navigator as any)?.userAgentData?.mobile;
  if (typeof userAgentDataMobile === "boolean") return userAgentDataMobile;

  const os = detectOS();
  if (os === "ios" || os === "android") return true;

  if (typeof navigator !== "undefined") {
    const ua = navigator.userAgent || "";
    if (/(iPhone|iPod|iPad|Android|Mobile|CriOS|FxiOS|EdgiOS)/i.test(ua)) {
      return true;
    }
  }

  if (typeof window !== "undefined") {
    const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
    const smallViewport = Math.min(window.innerWidth, window.innerHeight) <= 1024;
    const touchPoints = (navigator as any)?.maxTouchPoints ?? 0;
    if (coarsePointer && touchPoints > 0 && smallViewport) return true;
  }

  return false;
};

export interface DeviceHelp {
  os: DeviceOS;
  label: string;
  instructions: string[];
}

export const getDeviceHelp = (): DeviceHelp => {
  const os = detectOS();
  switch (os) {
    case "ios":
      return {
        os,
        label: "On iPhone or iPad",
        instructions: [
          "Open the page in Safari, tap aA in the address bar, then Show Reader, and screenshot it.",
          "Or tap Share → Print, pinch to zoom the preview, then Share as PDF.",
        ],
      };
    case "android":
      return {
        os,
        label: "On Android",
        instructions: [
          "Open the page in Chrome, tap the ⋮ menu → Share → Long Image for a full-page screenshot.",
          "Or tap ⋮ → Print → Save as PDF.",
        ],
      };
    case "mac":
      return {
        os,
        label: "On Mac",
        instructions: [
          "In Safari, choose File → Export as PDF.",
          "Or press ⌘ + Shift + 4 to screenshot the recipe area.",
        ],
      };
    case "windows":
      return {
        os,
        label: "On Windows",
        instructions: [
          "Press Win + Shift + S to snip the recipe section.",
          "Or in your browser press Ctrl + P → Save as PDF.",
        ],
      };
    default:
      return {
        os,
        label: "Tip",
        instructions: [
          "Use your browser's Print → Save as PDF.",
          "Or take a screenshot of the recipe section and upload it here.",
        ],
      };
  }
};
